import { useEffect, useState } from 'react';
import { CirclePlay } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import cyberTracerLogo from '../../assets/cyber-tracer-logo-header.png';
import { isMobileHowToClient, resolveHowToUrl } from '../../utils/howToVideo';

/**
 * @returns {string}
 */
function useHowToHref() {
  const [href, setHref] = useState(() => resolveHowToUrl(isMobileHowToClient()));

  useEffect(() => {
    const update = () => setHref(resolveHowToUrl(isMobileHowToClient()));
    update();
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const queries = ['(pointer: coarse)', '(max-width: 768px)'].map((query) => window.matchMedia(query));
    queries.forEach((mq) => {
      if (typeof mq.addEventListener === 'function') mq.addEventListener('change', update);
    });
    return () => {
      queries.forEach((mq) => {
        if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', update);
      });
    };
  }, []);

  return href;
}

export default function WiwInvestigationHeader({ onLogoClick }) {
  const navigate = useNavigate();
  const howToHref = useHowToHref();

  const handleLogoClick = () => {
    if (typeof onLogoClick === 'function') {
      onLogoClick();
      return;
    }
    navigate('/');
  };

  return (
    <header className="wiw-inv-header sticky top-0 z-40 flex w-full shrink-0 items-center gap-2 border-b border-white/10 bg-[#0f172a]/95 px-3 py-2 pt-[max(0.5rem,env(safe-area-inset-top))] backdrop-blur-xl sm:px-6 sm:py-2.5">
      <button
        type="button"
        onClick={handleLogoClick}
        className="group flex min-w-0 max-w-full items-center gap-3 rounded-xl border border-transparent py-0.5 pl-0.5 pr-2 text-left transition hover:border-cyan-500/25 hover:bg-white/[0.04]"
        aria-label="Cyber Tracer — reset workspace and return home"
      >
        <img
          src={cyberTracerLogo}
          alt="Cyber Tracer"
          className="h-8 w-auto max-w-[min(100%,240px)] object-contain object-left sm:h-10 sm:max-w-[300px]"
          style={{
            // Soft cyan lift to match page accents (#00f2ff / cyan-400)
            filter: 'drop-shadow(0 0 10px rgba(0, 242, 255, 0.22)) saturate(1.05) brightness(1.02)',
          }}
          draggable={false}
        />
        <span className="hidden min-w-0 border-l border-white/10 pl-3 font-mono text-[9px] uppercase tracking-[0.28em] text-slate-500 opacity-70 transition group-hover:text-cyan-400/80 group-hover:opacity-100 lg:block">
          Investigation OSINT
        </span>
      </button>
      <a
        href={howToHref}
        target="_blank"
        rel="noopener noreferrer"
        className="ml-auto inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-2.5 text-xs font-semibold text-slate-100 transition hover:border-cyan-400/40 hover:bg-cyan-500/10 hover:text-cyan-100 sm:px-3 sm:text-sm"
        aria-label="How it works, opens YouTube"
        data-testid="how-to-video-link"
      >
        <CirclePlay className="h-4 w-4 shrink-0" aria-hidden strokeWidth={2.25} />
        <span className="hidden sm:inline">How it works</span>
      </a>
    </header>
  );
}
